import { useEffect, useRef, useState } from 'react';

import type React from 'react';

import { fieldError, setClass } from '../../helpers/utils';
import { Eye as IconEye } from '../Icons/Eye';
import { EyeOff as IconEyeOff } from '../Icons/EyeOff';
import './styles.scss';

type InputProps = {
	className?: string;
	type?: string;
	id?: string;
	name?: string;
	value?: string | number;
	placeholder?: string;
	autoComplete?: string;
	disabled?: boolean;
	step?: string;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	size?: string;
	children?: React.ReactNode;
	positionIcon?: string;
	toggleShow?: boolean;
	label?: string;
	hookForm?: any;
};

export const Input = ({
	className = '',
	type = 'text',
	id,
	name,
	value,
	placeholder,
	autoComplete,
	disabled = false,
	step = '1',
	onBlur,
	onChange,
	onKeyDown,
	size = 'medium',
	children,
	positionIcon = 'right',
	toggleShow,
	label, // used for accordion-filters
	hookForm = { formState: { errors: {} } } as any,
}: InputProps) => {
	const [isFocus, setIsFocus] = useState(false);
	const [isFill, setIsFill] = useState(false);
	const inputEl = useRef(null);
	const [showPass, setShowPass] = useState(false);

	const {
		register,
		watch,
		formState: { errors },
	} = hookForm;

	const watchInput = watch?.(name, true);

	const typeSize = {
		medium: 'is-medium',
	};

	const isSize = typeSize[size] || '';

	const handleBlur = (event) => {
		setIsFocus(false);
		setIsFill(!isEmptyValue());

		if (typeof onBlur === 'function') {
			onBlur(event);
		}
	};

	const handleChange = (event) => {
		if (typeof onChange === 'function') {
			onChange(event);
		}
	};

	const handleKeyDown = (event) => {
		if (typeof onKeyDown === 'function') {
			onKeyDown(event);
		}
	};

	const isEmptyValue = () => {
		const elem = inputEl.current;
		const input = elem.querySelector('input');
		return !(input.value.trim().length > 0);
	};

	const handleShowPass = () => {
		inputEl.current.querySelector('input').type = showPass
			? 'text'
			: 'password';
		setShowPass(!showPass);
	};

	useEffect(() => {
		setIsFill(!isEmptyValue());
	}, []);

	useEffect(() => {
		setIsFill(!isEmptyValue());
	}, [watchInput]);

	return (
		<div
			ref={inputEl}
			className={setClass(`input
        ${isFill || isFocus ? 'is-fill' : ''}
        ${isSize}
        ${positionIcon ? `icon-${positionIcon}` : ''}
        ${fieldError(errors, name) ? 'is-error' : ''}
        ${className}`)}
		>
			{children}
			<label htmlFor={id}>{placeholder}</label>
			<input
				type={type}
				id={id}
				data-label={label}
				name={name}
				value={value}
				autoComplete={autoComplete}
				disabled={disabled}
				onFocus={() => setIsFocus(true)}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				step={step}
				{...register?.(name, {
					onBlur: (e) => handleBlur(e),
					onChange: (e) => handleChange(e),
				})}
			/>
			{toggleShow && (
				<div
					className="icon-show"
					onClick={() => handleShowPass()}
					data-testid="toggle-icon"
				>
					{showPass ? <IconEyeOff /> : <IconEye />}
				</div>
			)}
			{fieldError(errors, name) && (
				<span className="message-error">
					{(fieldError(errors, name) as any).message}
				</span>
			)}
		</div>
	);
};
